import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {AssetModel} from "../../models/asset.model";
import {AssetsService} from "../assets.service";
import {AssetsViewFilesComponent} from "../assets-view-files/assets-view-files.component";
import {
  AssetsViewCorrectiveActionsComponent
} from "../assets-view-corrective-actions/assets-view-corrective-actions.component";
import {AssetsViewContractorsComponent} from "../assets-view-contractors/assets-view-contractors.component";
import {AssetsViewUsersComponent} from "../assets-view-users/assets-view-users.component";
import {AssetsViewHazardsComponent} from "../assets-view-hazards/assets-view-hazards.component";
import {AssetsViewSafetyObservationsComponent} from "../assets-view-safety-observations/assets-view-safety-observations.component";
import {AssetsViewIncidentsComponent} from "../assets-view-incidents/assets-view-incidents.component";
import {AssetsClaimQrCodeComponent} from "../assets-claim-qr-code/assets-claim-qr-code.component";

@Component({
  selector: 'app-assets-view',
  templateUrl: './assets-view.component.html',
  styleUrls: ['./assets-view.component.scss']
})
export class AssetsViewComponent implements OnInit {

  asset: AssetModel = new AssetModel();

  @ViewChild('fileInput') fileInput: ElementRef;

  shareLink: string;
  readonly baseUrl = window.location.origin;

  // Index 0 = The asset details.
  @ViewChild('assetsViewFilesComponent') assetsViewFilesComponent: AssetsViewFilesComponent; // Index 1.
  @ViewChild('assetsViewContractorsComponent') assetsViewContractorsComponent: AssetsViewContractorsComponent; // Index 2.
  @ViewChild('assetsViewUsersComponent') assetsViewUsersComponent: AssetsViewUsersComponent; // Index 3.
  @ViewChild('assetsViewCorrectiveActionsComponent') assetsViewCorrectiveActionsComponent: AssetsViewCorrectiveActionsComponent; // Index 4.
  @ViewChild('assetsViewHazardsComponent') assetsViewHazardsComponent: AssetsViewHazardsComponent; // Index 5.
  @ViewChild('assetsViewSafetyObservationsComponent') assetsViewSafetyObservationsComponent: AssetsViewSafetyObservationsComponent; // Index 6.
  @ViewChild('assetsViewIncidentsComponent') assetsViewIncidentsComponent: AssetsViewIncidentsComponent; // Index 7.

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetsService: AssetsService,
    private dialogRef: MatDialogRef<AssetsViewComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) { }

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;

    if (typeof asset_id === 'number' && asset_id > 0) {
      this.find(asset_id);
    }
  }

  /**
   * Finds a Asset by its ID.
   *
   * @param {number} id - The ID of the Vendor.
   * @private
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/assets/${id}`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.setShareLink();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Build the share / copy link from the asset's QR code hash. The QR encodes only the
   * code's own code_hash, so the share link resolves through the same handle-qr-code path
   * as a scanned code.
   *
   * @return {void}
   */
  private setShareLink(): void {
    this.shareLink = this.asset?.qr_code?.code_hash
      ? this.baseUrl + `/handle-qr-code/${this.asset.qr_code.code_hash}`
      : '';
  }

  /**
   * When tabs are changed, reload lists. If the tab order changes this need to be updated.
   *
   * @param tabData The tab to check.
   */
  onTabChanged(tabData: any): void {
    // index 0 is the asset view tab.
    switch ( tabData.index ) {
      case 1:
        this.assetsViewFilesComponent.dataSource.getData();
        break;
      case 2:
        this.assetsViewContractorsComponent.dataSource.getData();
        break;
      case 3:
        this.assetsViewUsersComponent.dataSource.getData();
        break;
      case 4:
        this.assetsViewCorrectiveActionsComponent.dataSource.getData();
        break;
      case 5:
        this.assetsViewHazardsComponent.dataSource.getData();
        break;
      case 6:
        this.assetsViewSafetyObservationsComponent.dataSource.getData();
        break;
      case 7:
        this.assetsViewIncidentsComponent.dataSource.getData();
        break;
      default:
      // No action needed.
    }
  }

  /**
   * Click an ion-button that in turn triggers the file input type to select a file.
   * when a file is selected, it is uploaded.
   *
   * @param evt
   */
  onTriggerFileSelector(evt: Event): void {
    if(this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  /**
   * Method to handle the upload of a selected file.
   *
   * @param evt Event object containing information about the file selection.
   * @return void
   */
  onUploadSelectedFile(evt: any): void {
    this.api.makeUploadRequest(`v2/assets/${this.asset.id}/main-image`, evt.target.files[0])
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.utils.showToast('Your main image was updated.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Remove the main image of the asset.
   *
   * @return {void}
   */
  onRemoveMainImage(): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset.id}/main-image`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.utils.showToast('Your main image was removed.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Regenerates the QR Code for the current asset.
   *
   * @return {void}
   */
  onRegenerateQrCode(): void {
    this.api.makeRequest('put', `v2/assets/${this.asset.id}/qr-code`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        this.setShareLink();

        this.utils.showToast('Your QR Code was regenerated.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Claim a pre-printed QR code for this asset by its printed short hash.
   *
   * Prompts for the printed short hash (paper id), calls the claim endpoint, and reloads
   * the asset on success so the claimed code is shown.
   *
   * @return {void}
   */
  onClaimQrCode(): void {
    this.utils.showComponentDialog(
      AssetsClaimQrCodeComponent,
      {},
      { width: '420px' },
      (paper_id: string) => {
        if ( !paper_id ) {
          return;
        }

        this.api.makeRequest('put', `v2/assets/${this.asset.id}/qr-code/claim`, {
          paper_id: paper_id
        })
          .then((response) => {
            this.asset = response.data as AssetModel;

            this.setShareLink();

            this.utils.showToast('The QR Code was successfully claimed for this asset.');
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }

  /**
   * Notifies the user that the link has been copied to the clipboard.
   *
   * @return {void}
   */
  notifyCopied(): void {
    this.utils.showToast('Copied link to clipboard');
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void}
   */
  onOpenUrl(url: string): void {
    this.utils.openUrl(url);
  }
}
